import { Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { RewardListItem } from "src/api/reward/rewardApi";
import type { RewardItemDetail } from "src/types/reward";
import { ManageBtns } from "src/components/common/TableButtons";
import CustomButton from "src/components/common/CustomButton";

type Props = {
  list: RewardListItem[];
  loading: boolean;
  disabled?: boolean;
  onEdit: (item: RewardListItem) => void;
  onDelete: (item: RewardListItem) => void;
};

const rewardTypeLabel: Record<string, string> = {
  POINT: "포인트",
  COUPON: "쿠폰",
};

const itemSummary = (item: RewardItemDetail) => {
  const base =
    item.rewardType === "POINT"
      ? `${(item.pointAmount ?? 0).toLocaleString()}P`
      : item.couponName || `쿠폰 #${item.couponId ?? "-"}`;
  return item.quantity && item.quantity > 1 ? `${base} x${item.quantity}` : base;
};

export default function RewardTable({ list, loading, disabled = false, onEdit, onDelete }: Props) {
  const columns: ColumnsType<RewardListItem> = [
    {
      title: "ID",
      dataIndex: "rewardId",
      align: "center",
      width: 80,
    },
    {
      title: "리워드명",
      dataIndex: "rewardName",
      align: "center",
      width: 220,
      render: (value) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: "리워드 코드",
      dataIndex: "rewardCode",
      align: "center",
      width: 180,
      render: (value) => (
        <Typography.Text style={{ fontFamily: "monospace", fontSize: 12 }}>{value}</Typography.Text>
      ),
    },
    {
      title: "구성",
      dataIndex: "items",
      align: "center",
      width: 120,
      render: (items: RewardItemDetail[]) => {
        const count = items?.length ?? 0;
        if (count === 0) return <Typography.Text type="secondary">-</Typography.Text>;
        return count > 1 ? (
          <Tag color="purple">쿠폰팩 ({count}개)</Tag>
        ) : (
          <Tag color={items[0].rewardType === "POINT" ? "blue" : "green"}>
            {rewardTypeLabel[items[0].rewardType] || items[0].rewardType}
          </Tag>
        );
      },
    },
    {
      title: "보상 항목",
      dataIndex: "items",
      align: "center",
      width: 260,
      render: (items: RewardItemDetail[]) =>
        items && items.length > 0 ? (
          <Space direction="vertical" size={4} style={{ display: "flex" }}>
            {items.map((item) => (
              <Tag
                key={item.itemId}
                color={item.rewardType === "POINT" ? "blue" : "green"}
                style={{ marginInlineEnd: 0 }}
              >
                {itemSummary(item)}
              </Tag>
            ))}
          </Space>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
    {
      title: "활성 상태",
      dataIndex: "isActive",
      align: "center",
      width: 100,
      render: (value) => <Tag color={value ? "green" : "default"}>{value ? "활성" : "비활성"}</Tag>,
    },
    {
      title: "생성일",
      dataIndex: "createdAt",
      align: "center",
      width: 190,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "관리",
      dataIndex: "actions",
      width: 150,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <ManageBtns style={{ justifyContent: "flex-end" }}>
          <CustomButton onClick={() => onEdit(record)} disabled={disabled || loading}>
            수정
          </CustomButton>
          <CustomButton
            customVariant="danger"
            onClick={() => onDelete(record)}
            disabled={disabled || loading}
          >
            삭제
          </CustomButton>
        </ManageBtns>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={list}
      rowKey={(record) => String(record.rewardId)}
      pagination={false}
      loading={loading}
      locale={{ emptyText: "데이터가 없습니다." }}
      scroll={{ x: 1000 }}
    />
  );
}
